import { useMemo } from 'react';

import { normalizeToUnixSeconds } from '../../../utils/dateUtils';

export type SolarLinePoint = {
  x: string;
  y: number;
  _series: 'solarGeneration';
  _solarKwh: number;
};

type ChartDataPoint = {
  x: string;
  y?: number;
  _ts?: number;
  data?: {
    solar_generation?: number | string | null;
    expected_generation_kw?: number | string | null;
    target_datetime_utc?: number | string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type SolarLineResult = {
  solarLineData: SolarLinePoint[];
  solarMaxKwh: number;
  solarLineDataLeft: SolarLinePoint[];
};

const EMPTY_RESULT: SolarLineResult = { solarLineData: [], solarMaxKwh: 0, solarLineDataLeft: [] };

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Derives the solar generation overlay series from the combined behaviour chart data.
 *
 * Historic points use `solar_generation` (Wh for the interval).
 * Future points use `expected_generation_kw` (power; converted to kWh over the interval).
 *
 * The series is normalised to [0, 100] so it can share the SOC chart's y-axis,
 * with a separate right-hand axis showing the real kWh values.
 *
 * `solarLineDataLeft` is the slice up to and including `intersectionX` (used to draw
 * a solid historic line behind the dashed future line).
 */
export const useSolarLine = (
  behaviorChartData: ChartDataPoint[] | null | undefined,
  chartAvailable: boolean,
  showSolarGeneration: boolean,
  intersectionX: string | null | undefined
): SolarLineResult => {
  const solarResult = useMemo((): Omit<SolarLineResult, 'solarLineDataLeft'> => {
    if (!chartAvailable || !showSolarGeneration || !behaviorChartData?.length) {
      return { solarLineData: [], solarMaxKwh: 0 };
    }

    const points = behaviorChartData;

    const intermediate = points.map((point, idx) => {
      // Historic: solar_generation is energy in Wh for the interval.
      const historicSolarWh = toNumberOrNull(point.data?.solar_generation);
      if (typeof historicSolarWh === 'number' && historicSolarWh > 0) {
        return { x: point.x, _solarKwh: historicSolarWh / 1000, _ts: point._ts };
      }

      // Forecast: expected_generation_kw is power; derive energy over the interval.
      const kw = toNumberOrNull(point.data?.expected_generation_kw);
      const currentTs = normalizeToUnixSeconds(point.data?.target_datetime_utc);

      if (typeof kw === 'number' && typeof currentTs === 'number') {
        let nextTs: number | null = null;
        for (let j = idx + 1; j < points.length; j++) {
          const candidate = normalizeToUnixSeconds(points[j].data?.target_datetime_utc);
          if (typeof candidate === 'number') {
            nextTs = candidate;
            break;
          }
        }
        const intervalSeconds = typeof nextTs === 'number' && nextTs > currentTs ? nextTs - currentTs : 1800;
        return { x: point.x, _solarKwh: Math.max(0, kw) * (intervalSeconds / 3600), _ts: point._ts };
      }

      return { x: point.x, _solarKwh: 0, _ts: point._ts };
    });

    const maxKwh = intermediate.reduce(
      (max, d) => (Number.isFinite(d._solarKwh) ? Math.max(max, d._solarKwh) : max),
      0
    );
    if (maxKwh <= 0) return { solarLineData: [], solarMaxKwh: 0 };

    const sorted = [...intermediate].sort((a, b) => (a._ts ?? 0) - (b._ts ?? 0));

    return {
      solarMaxKwh: maxKwh,
      solarLineData: sorted.map((d) => ({
        x: d.x,
        y: (d._solarKwh / maxKwh) * 100,
        _series: 'solarGeneration' as const,
        _solarKwh: d._solarKwh,
      })),
    };
  }, [chartAvailable, showSolarGeneration, behaviorChartData]);

  const solarLineDataLeft = useMemo(() => {
    const { solarLineData } = solarResult;
    if (!solarLineData.length || !intersectionX) return [];
    const splitIndex = solarLineData.findIndex((d) => d.x === intersectionX);
    if (splitIndex < 0) return [];
    return solarLineData.slice(0, splitIndex + 1);
  }, [solarResult, intersectionX]);

  if (!solarResult.solarLineData.length) return EMPTY_RESULT;

  return { ...solarResult, solarLineDataLeft };
};
